// One block of the editor (decision 034): its editable content, the «+» and the handle at its left on hover, the marker of a list.

import { memo, useLayoutEffect, useRef } from 'react';
import { inlineToHtml, isImage, type Block } from '@confdeck/core-shared';
import { Icon } from '../ui/icons.tsx';
import { Checkbox, cx } from '../ui/primitives.tsx';
import { kindDef } from './kinds.ts';
import { handleInput, handleKey, handlePaste } from './keys.ts';
import type { EditorCtl } from './ctl.ts';
import s from './editor.module.css';

export interface RowEnv {
  ctl: EditorCtl;
  readOnly: boolean;
  /** The title of a linked record now (it may have been renamed since the link was made). */
  titleOf: (id: string) => string | undefined;
  onOpenRecord?: (id: string) => void;
  onFiles: (id: string, files: File[]) => void;
  onPlus: (id: string) => void;
  placeholder: string;
}

const TAG: Partial<Record<Block['kind'], string>> = { h1: 'h2', h2: 'h3', h3: 'h4' };

function Editable({ b, env, solo }: { b: Block; env: RowEnv; solo: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const shown = useRef<string | null>(null);
  const shownIn = useRef<HTMLElement | null>(null);
  const { ctl, readOnly } = env;
  // The DOM is the person's while they type: it is rewritten only when the text changed elsewhere (a split, a merge, a reset),
  // or when the block got a new element — another kind is another tag (a paragraph made a heading starts empty otherwise).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || (shown.current === b.text && shownIn.current === el)) return;
    if (b.kind === 'code') el.textContent = b.text; else el.innerHTML = inlineToHtml(b.text);
    shown.current = b.text;
    shownIn.current = el;
  });
  const placeholder = solo ? env.placeholder : kindDef(b.kind).placeholder ?? (b.kind === 'p' ? env.placeholder : '');
  const props = {
    ref: ref as React.RefObject<never>,
    'data-edit': '',
    'data-placeholder': placeholder,
    className: cx(s.edit, s[b.kind], solo && s.solo, b.kind === 'todo' && b.checked && s.done),
    contentEditable: !readOnly,
    suppressContentEditableWarning: true,
    spellCheck: b.kind !== 'code',
    onInput: (e: React.FormEvent<HTMLElement>) => { handleInput(ctl, e.currentTarget, ctl.get(b.id)!); shown.current = ctl.get(b.id)?.text ?? null; },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => { if (!readOnly) handleKey(ctl, e, ctl.get(b.id)!); },
    onPaste: (e: React.ClipboardEvent<HTMLElement>) => { if (!readOnly) handlePaste(ctl, e, ctl.get(b.id)!, env.onFiles); },
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      const a = (e.target as HTMLElement).closest('a');
      if (!a) return;
      // A link opens on a click while reading, and on Ctrl/⌘+click while writing (a plain click puts the caret into it).
      if (readOnly || e.metaKey || e.ctrlKey) {
        e.preventDefault();
        const rec = a.getAttribute('data-record');
        if (rec && env.onOpenRecord) env.onOpenRecord(rec); else window.open(a.href, '_blank', 'noreferrer');
      }
    },
  };
  if (b.kind === 'code') return <pre {...props} />;
  const Tag = (TAG[b.kind] ?? 'div') as 'div';
  return <Tag {...props} />;
}

function Piece({ b, env }: { b: Block; env: RowEnv }) {
  const { ctl, readOnly } = env;
  const props = { 'data-edit': '', tabIndex: readOnly ? -1 : 0, onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => { if (!readOnly) handleKey(ctl, e, b); } };
  if (b.kind === 'divider') return <div {...props} className={s.divider} role="separator"><hr /></div>;
  if (b.kind === 'record') {
    const title = env.titleOf(b.recordId!) ?? b.text;
    return <div {...props} className={s.piece}><button type="button" className={s.recLink} onClick={() => env.onOpenRecord?.(b.recordId!)}><Icon name="link" size={14} />{title || 'Запись'}</button></div>;
  }
  const preview = ctl.previews.get(b.url ?? '');
  const uploading = !!preview;
  if (isImage(b)) return <figure {...props} className={cx(s.piece, s.figure)}><img src={preview ?? b.url} alt={b.text} className={cx(uploading && s.uploading)} />{uploading && <figcaption className={s.caption}>Загружается…</figcaption>}</figure>;
  return <div {...props} className={s.piece}><a className={s.file} href={b.url} download={b.text}><Icon name="paperclip" size={14} />{b.text || 'Файл'}</a>{uploading && <span className={s.caption}>Загружается…</span>}</div>;
}

const isText = (k: Block['kind']) => k !== 'divider' && k !== 'file' && k !== 'record';

export const BlockRow = memo(function BlockRow({ b, num, env, solo, drop, children }: { b: Block; num: number; env: RowEnv; solo: boolean; drop: 'before' | 'after' | null; children?: React.ReactNode }) {
  const { ctl, readOnly } = env;
  return (
    <div className={cx(s.row, s[`row_${b.kind}`], drop === 'before' && s.dropBefore, drop === 'after' && s.dropAfter)} data-bid={b.id}>
      {!readOnly && (
        <div className={s.gutter} contentEditable={false}>
          {(!ctl.kinds || ctl.kinds.size > 1) && <button type="button" className={s.handle} tabIndex={-1} aria-label="Добавить блок ниже" title="Добавить блок ниже" onClick={() => env.onPlus(b.id)}><Icon name="plus" size={15} /></button>}
          {children}
        </div>
      )}
      {b.kind === 'bullet' && <span className={s.marker} contentEditable={false}>•</span>}
      {b.kind === 'number' && <span className={cx(s.marker, s.num)} contentEditable={false}>{num}.</span>}
      {b.kind === 'todo' && (
        <span className={s.marker} contentEditable={false}>
          <Checkbox checked={!!b.checked} disabled={readOnly} label="Готово" onChange={() => ctl.patch(b.id, { checked: !b.checked })} />
        </span>
      )}
      {b.kind === 'callout' && <span className={s.marker} contentEditable={false}><Icon name="callout" size={16} /></span>}
      {isText(b.kind) ? <Editable b={b} env={env} solo={solo} /> : <Piece b={b} env={env} />}
    </div>
  );
});
